import { router } from '@inertiajs/react';
import equal from 'fast-deep-equal';
import { v7 as uuidv7 } from 'uuid';
import { create } from 'zustand';

import { CHILD_TO_INDEX_TYPE } from '@/layouts/app/sidebar/page-tree-sidebar/page-utils';
import { removeBlockSubtreeAndCleanup } from '@/lib/blocks/cleanup';
import {
  isDataWidgetBlock,
  resolveDataWidgetProps,
  stripPreviewItemsFromBlock,
} from '@/lib/blocks/collection/data-widget/blocks';
import { normalizeDefaultBlocks } from '@/lib/blocks/default-blocks';
import {
  buildImageSuggestionsPageSummary,
  type ImageSuggestionsPageSummary,
} from '@/lib/blocks/image-suggestion-context';
import { resolveBlockStackLayout } from '@/lib/blocks/layout/block-stack-layout';
import {
  applyBlockPropsWithFloatLayout,
  hasFloatLayoutProps,
  invalidateFloatRestorePositionsForChangedSiblings,
} from '@/lib/blocks/layout/float-layout';
import { normalizeBlocksStructure } from '@/lib/blocks/normalization';
import { resolveBlockProps } from '@/lib/blocks/prop-resolver';
import {
  reorderSectionWithViewportRetain,
  scheduleScrollToPageEditorSection,
} from '@/lib/page-editor/scroll-to-section';
import {
  buildPreviewNavigation,
  getMainSections,
  getSortedSections,
  normalizePage,
  remapPageContentIds,
  resolveSectionPropsForInsert,
  shouldSyncPreviewNavigation,
  updateMainSections,
} from '@/lib/page-utils';
import { withRemovalsAsNull } from '@/lib/prop-utils';
import { cmsPagesEditorPath } from '@/lib/routes';
import { isSpecialPageLayoutSection } from '@/lib/special-page-layouts';
import { useNestedPageViewStore } from '@/stores/nested-page-view-store';
import {
  handlePageEditorAction,
  markPersistedPageContentChange,
} from '@/stores/page-editor-middleware';
import {
  mergeIdMappings,
  remapParentBlockId,
  resolveRemappedId,
} from '@/stores/page-editor-middleware/autosave-id-remap';
import { usePagesStore } from '@/stores/pages-store';
import { getBlockUiKey, type PageBlock } from '@/types/block';
import type { ImageSuggestionTarget } from '@/types/image';
import type { EditingSlot, Page, PageSeoMetadata, PageType, SetPageOptions } from '@/types/page';
import { PageEditorActionType } from '@/types/page-editor-actions';
import type { PageSection } from '@/types/page-section';
import { getConflictingProposalIdsForUndo } from './ai-proposals/undo-helpers';
import { getAiProposalsAdapter } from './page-editor/ai-adapter';
import { PAGE_EDITOR_HISTORY_TARGET_IDS } from './page-editor/constants';

export { PAGE_EDITOR_HISTORY_TARGET_IDS } from './page-editor/constants';

import {
  type HistoryKind,
  type PersistedPageEditorHistory,
  purgeOtherEditorHistoryAccounts,
  readPersistedEditorHistory,
  registerHistoryAccountIdProvider,
  registerPageHistoryApplier,
  registerPageHistoryProvider,
  scheduleEditorHistoryWrite,
  withApplyingPersistedEditorHistory,
} from './editor-history-storage';

// TODO: Break down this store into focused modules:
// page state, history management, section operations, block operations.

const HISTORY_LIMIT = 50;

type AddSectionOptions = {
  skipDefaultHydration?: boolean;
  hydrateDataWidgets?: boolean;
};

interface FloatLayoutPreviewBaseline {
  sectionId: string;
  blockId: string;
  blocks: PageBlock[];
}

/**
 * Guard: this store is mounted in read-only surfaces (preview iframe,
 * public render) so shared components can read the page. Only the
 * actual editor window should capture history/autosave, so we detect
 * that environment before enabling those side effects.
 */
const isEditorWindow =
  typeof globalThis === 'undefined' || typeof globalThis.window === 'undefined'
    ? true
    : (() => {
        try {
          const win = globalThis.window;
          return win.self === win.top;
        } catch {
          return true;
        }
      })();

// NOTE: If snapshot size becomes a bottleneck, consider restructuring the
// history store (e.g., section-level diffs) instead of swapping clone
// strategies. That would let us capture smaller patches without deep copies.
// slots.header/footer are site-styles config denormalized onto Page for
// rendering; only slots.main is page-owned. Excluding header/footer from page
// snapshots keeps a page undo from rolling back unrelated site-styles edits
// via the slot-mirroring effect in editor.tsx.
const stripSiteOwnedFields = (page: Page): Page => {
  if (!page.slots) return page;
  const { header: _h, footer: _f, ...restSlots } = page.slots;
  return { ...page, slots: restSlots as Page['slots'] };
};

const clonePageSnapshot = (() => {
  const hasStructuredClone = typeof structuredClone === 'function';

  return (page: Page): Page => {
    const snapshot = hasStructuredClone
      ? structuredClone(page)
      : (JSON.parse(JSON.stringify(page)) as Page);
    return stripSiteOwnedFields(normalizePage(snapshot));
  };
})();

function sectionPropsForContentModelDraft(
  existingProps: PageSection['props'] | undefined,
  contentModel: Record<string, unknown>,
) {
  return {
    ...(existingProps || {}),
    contentModel,
  };
}

// 'page' = entry maps to a Page snapshot in historyPast/Future.
// 'site' = entry maps to a site-styles snapshot held inside site-styles-store
// via a registered adapter. The two kinds are interleaved chronologically in
// `historyOrder` so undo/redo replays in the order the user made changes.
type PendingCrossPageHistoryAction = 'undo' | 'redo';

export interface SiteHistoryAdapter {
  // Pop the most recent past entry, push current to future, apply the popped
  // snapshot to the site styles store, and persist it. Returns true on success.
  undo: () => boolean;
  redo: () => boolean;
  canUndo: () => boolean;
  canRedo: () => boolean;
  // Drop any pending site redo entries so they can't be replayed after a
  // page edit has invalidated the unified historyOrderFuture stack.
  clearFuture: () => void;
}

interface PageEditorStore {
  // State
  page: Page | null;
  isGeneratingSeo: boolean;
  previewNavigationPage: Pick<Page, 'id' | 'title' | 'navigationTitle'> | null;
  historyHighlightSectionId: string | null;
  historyHighlightVersion: number;
  historyPast: Page[];
  historyFuture: Page[];
  historyPastTargets: (string | null)[];
  historyFutureTargets: (string | null)[];
  historyOrder: HistoryKind[];
  historyOrderFuture: HistoryKind[];
  siteHistoryAdapter: SiteHistoryAdapter | null;
  pendingCrossPageHistoryAction: PendingCrossPageHistoryAction | null;
  pendingCrossPageHistoryPageId: string | null;
  editingSlot: EditingSlot | null;
  incomingSectionIdMappings: Record<string, string>;
  incomingBlockIdMappings: Record<string, string>;
  registerSiteHistoryAdapter: (adapter: SiteHistoryAdapter | null) => void;
  recordSiteHistoryEntry: () => void;
  historyAccountId: string | null;
  initHistoryScope: (accountId: string) => void;

  // Actions - atomic state updates only
  setPage: (page: Page | null, options?: SetPageOptions) => void;
  /** Returns committed page state, excluding transient float-layout previews. */
  getPageForAutosave: () => Page | null;
  setPageForAutosaveRemap: (
    sectionsMapping: Record<string, string>,
    blocksMapping?: Record<string, string>,
  ) => void;
  setEditingSlot: (slot: EditingSlot | null) => void;
  setSlotVisibility: (slot: 'header' | 'footer', visible: boolean) => void;
  addSection: (
    section: Omit<PageSection, 'id' | 'rowPosition'> & { id?: string },
    insertRowPosition?: number,
    options?: AddSectionOptions,
  ) => Promise<string | null>;
  previewAddSection: (
    section: Omit<PageSection, 'id' | 'rowPosition'> & { id?: string },
    insertRowPosition?: number,
    options?: AddSectionOptions,
  ) => Promise<string | null>;
  addBlock: (sectionId: string, block: Omit<PageBlock, 'id'> & { id?: string }) => string | null;
  /** Commits a block update and applies any float-driven layout changes atomically. */
  updateBlock: (sectionId: string, blockId: string, updates: Partial<PageBlock>) => void;
  /** Previews props and float-driven layout without adding history or autosaving. */
  previewBlockPropsWithFloatLayout: (
    sectionId: string,
    blockId: string,
    props: Record<string, unknown>,
  ) => void;
  /** Cancels a transient float-layout preview and restores its committed block layout. */
  cancelBlockPropsWithFloatLayoutPreview: (sectionId: string, blockId: string) => void;
  updateBlockPreview: (sectionId: string, blockId: string, updates: Partial<PageBlock>) => void;
  batchUpdateBlocks: (
    sectionId: string,
    updates: Array<{ blockId: string; updates: Partial<PageBlock> }>,
  ) => void;
  updateBlocks: (sectionId: string, blocks: PageBlock[]) => void;
  deleteBlock: (
    sectionId: string,
    blockId: string,
    options?: { preserveEmptyParentIds?: ReadonlySet<string> },
  ) => void;
  updateSection: (
    sectionId: string,
    updates: Partial<PageSection>,
    options?: { skipHistory?: boolean },
  ) => void;
  updateSectionPreview: (sectionId: string, updates: Partial<PageSection>) => void;
  replaceSectionPreview: (sectionId: string, snapshot: Partial<PageSection>) => void;
  updateSectionFromServer: (sectionId: string, updates: Partial<PageSection>) => void;
  updateContentModelDraft: (sectionId: string, contentModelPatch: Record<string, unknown>) => void;
  syncPageLevelContentModel: (contentModelPatch: Record<string, unknown>) => void;
  deleteSection: (sectionId: string) => void;
  removePreviewSection: (sectionId: string) => void;
  replaceSection: (
    sectionId: string,
    section: Omit<PageSection, 'id' | 'rowPosition'> & Partial<Pick<PageSection, 'rowPosition'>>,
  ) => void;
  moveSectionUp: (sectionId: string) => void;
  moveSectionDown: (sectionId: string) => void;
  reorderSections: (sectionIds: string[]) => void;
  moveSectionsToRowPositions: (
    moves: Array<{ sectionId: string; targetRowPosition: number }>,
  ) => void;
  pendingUndoConfirmation: boolean;
  pendingUndoProposalIds: string[];
  beginHistoryGroup: () => void;
  endHistoryGroup: () => void;
  undo: () => void;
  confirmUndo: () => Promise<void>;
  cancelUndo: () => void;
  redo: () => void;
  clearPageHistory: () => void;
  clearDeletedPageHistory: (pageId: string) => void;
  clearPublishedHistory: () => void;
  setGeneratingSeo: (value: boolean) => void;
  updateSeoFromServer: (seo: PageSeoMetadata | undefined) => void;
  markHistoryHighlightTarget: (targetId: string) => void;
  clearHistoryHighlight: () => void;
  clearHistory: () => void;
  reset: () => void;
}

export interface SuggestionsPageSummarySection {
  id: string;
  sectionTemplateCode: string;
  variant: string | null;
  rowPosition: number;
  visible: boolean;
}

export interface SuggestionsPageSummary {
  pageId: string;
  pageType: string;
  sectionCount: number;
  sections: SuggestionsPageSummarySection[];
}

// Persisted history is loaded lazily once the editor calls initHistoryScope
// with the active accountId — the store is also mounted in non-editor surfaces
// where we don't know (or want) the scope.
const persistedPageEditorHistory: PersistedPageEditorHistory = {
  historyPast: [],
  historyFuture: [],
  historyPastTargets: [],
  historyFutureTargets: [],
  historyOrder: [],
  historyOrderFuture: [],
};

export const usePageEditorStore = create<PageEditorStore>((set, get) => {
  const floatLayoutPreviewBaselines = new Map<string, FloatLayoutPreviewBaseline>();
  const getFloatLayoutPreviewKey = (sectionId: string, blockId: string): string =>
    `${sectionId}\0${blockId}`;

  const hydrateInsertedDataWidgetBlocks = async (
    blocks: PageBlock[],
    pageId?: string,
    sectionTemplateCode?: string,
  ): Promise<PageBlock[]> => {
    if (!pageId || !blocks.some((block) => isDataWidgetBlock(block))) {
      return blocks;
    }

    const initialResolvedPropsById = await resolveBlockProps(
      blocks,
      pageId,
      undefined,
      sectionTemplateCode,
    );
    const blocksForDynamicResolution = blocks.map((block) => {
      if (!isDataWidgetBlock(block)) return block;
      if (!initialResolvedPropsById.get(block.id)?.hasDynamicRecords) {
        return {
          ...block,
          props: { ...(block.props ?? {}), ...(initialResolvedPropsById.get(block.id) ?? {}) },
        };
      }

      const widgetProps = resolveDataWidgetProps(block);
      return {
        ...block,
        props: {
          ...(block.props ?? {}),
          source: {
            mode: 'dynamic',
            strategy: widgetProps.source.strategy,
            limit: widgetProps.source.limit,
            filters: widgetProps.source.filters,
          },
        },
      };
    });

    const resolvedPropsById = await resolveBlockProps(
      blocksForDynamicResolution,
      pageId,
      undefined,
      sectionTemplateCode,
    );
    return blocksForDynamicResolution.map((block) =>
      stripPreviewItemsFromBlock({
        ...block,
        props: { ...(block.props ?? {}), ...(resolvedPropsById.get(block.id) ?? {}) },
      }),
    );
  };

  const prepareHistorySnapshot = () => {
    if (!isEditorWindow) {
      return null;
    }
    const currentPage = get().getPageForAutosave();
    return currentPage ? clonePageSnapshot(currentPage) : null;
  };

  const trimHistory = <T>(history: T[]): T[] => {
    if (history.length > HISTORY_LIMIT) {
      return history.slice(history.length - HISTORY_LIMIT);
    }
    return history;
  };

  const clearPageHistoryState = (state: PageEditorStore): PageEditorStore => ({
    ...state,
    historyHighlightSectionId: null,
    historyPast: [],
    historyFuture: [],
    historyPastTargets: [],
    historyFutureTargets: [],
    historyOrder: state.historyOrder.filter((kind) => kind === 'site'),
    historyOrderFuture: state.historyOrderFuture.filter((kind) => kind === 'site'),
    pendingCrossPageHistoryAction: null,
    pendingCrossPageHistoryPageId: null,
    pendingUndoConfirmation: false,
    pendingUndoProposalIds: [],
  });

  const markHistoryHighlight = (sectionId: string): void => {
    set((state) => ({
      historyHighlightSectionId: sectionId,
      historyHighlightVersion: state.historyHighlightVersion + 1,
    }));
  };

  const filterPageHistory = ({
    order,
    pageSnapshots,
    pageTargets,
    shouldRemovePage,
    removeSiteEntries = false,
  }: {
    order: HistoryKind[];
    pageSnapshots: Page[];
    pageTargets: (string | null)[];
    shouldRemovePage: (snapshot: Page) => boolean;
    removeSiteEntries?: boolean;
  }): {
    order: HistoryKind[];
    pageSnapshots: Page[];
    pageTargets: (string | null)[];
  } => {
    const nextOrder: HistoryKind[] = [];
    const nextPageSnapshots: Page[] = [];
    const nextPageTargets: (string | null)[] = [];
    let pageIndex = 0;

    for (const kind of order) {
      if (kind === 'site') {
        if (!removeSiteEntries) {
          nextOrder.push('site');
        }
        continue;
      }

      const snapshot = pageSnapshots[pageIndex];
      const target = pageTargets[pageIndex] ?? null;
      pageIndex += 1;

      if (!snapshot || shouldRemovePage(snapshot)) {
        continue;
      }

      nextOrder.push('page');
      nextPageSnapshots.push(snapshot);
      nextPageTargets.push(target);
    }

    return { order: nextOrder, pageSnapshots: nextPageSnapshots, pageTargets: nextPageTargets };
  };

  const clearPublishedHistoryState = (state: PageEditorStore): PageEditorStore => ({
    ...state,
    historyPast: [],
    historyFuture: [],
    historyPastTargets: [],
    historyFutureTargets: [],
    historyOrder: [],
    historyOrderFuture: [],
    pendingCrossPageHistoryAction: null,
    pendingCrossPageHistoryPageId: null,
    pendingUndoConfirmation: false,
    pendingUndoProposalIds: [],
  });

  const clearDeletedPageHistoryState = (
    state: PageEditorStore,
    deletedPageId: string,
  ): PageEditorStore => {
    const past = filterPageHistory({
      order: state.historyOrder,
      pageSnapshots: state.historyPast,
      pageTargets: state.historyPastTargets,
      shouldRemovePage: (snapshot) => snapshot.id === deletedPageId,
    });
    const future = filterPageHistory({
      order: state.historyOrderFuture,
      pageSnapshots: state.historyFuture,
      pageTargets: state.historyFutureTargets,
      shouldRemovePage: (snapshot) => snapshot.id === deletedPageId,
    });

    return {
      ...state,
      historyPast: past.pageSnapshots,
      historyFuture: future.pageSnapshots,
      historyPastTargets: past.pageTargets,
      historyFutureTargets: future.pageTargets,
      historyOrder: past.order,
      historyOrderFuture: future.order,
      pendingCrossPageHistoryAction:
        state.pendingCrossPageHistoryPageId === deletedPageId
          ? null
          : state.pendingCrossPageHistoryAction,
      pendingCrossPageHistoryPageId:
        state.pendingCrossPageHistoryPageId === deletedPageId
          ? null
          : state.pendingCrossPageHistoryPageId,
      pendingUndoConfirmation: false,
      pendingUndoProposalIds: [],
    };
  };

  const commitHistorySnapshot = (page: Page | null, targetId: string | null) => {
    if (!isEditorWindow || !page) {
      return;
    }
    set((state) => ({
      ...state,
      historyPast: trimHistory([...state.historyPast, page]),
      historyPastTargets: trimHistory([...state.historyPastTargets, targetId]),
      historyFuture: [],
      historyFutureTargets: [],
      historyOrder: state.historyOrder.concat('page').slice(-HISTORY_LIMIT),
      historyOrderFuture: [],
    }));
    get().siteHistoryAdapter?.clearFuture();
  };

  const pagesEquivalent = (a: Page, b: Page): boolean =>
    JSON.stringify(stripSiteOwnedFields(normalizePage(a))) ===
    JSON.stringify(stripSiteOwnedFields(normalizePage(b)));

  const sectionsEqual = (a: PageSection | undefined, b: PageSection | undefined): boolean =>
    JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

  const slotValuesEqual = (a: unknown, b: unknown): boolean =>
    JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

  const resolveHistoryHighlightSectionId = (
    beforeSections: PageSection[],
    afterSections: PageSection[],
  ): string | null => {
    const beforeMap = new Map(beforeSections.map((section) => [section.id, section]));
    const afterMap = new Map(afterSections.map((section) => [section.id, section]));

    const addedSection = afterSections.find((section) => !beforeMap.has(section.id));
    if (addedSection) {
      return addedSection.id;
    }

    const hasRemoval = beforeSections.some((section) => !afterMap.has(section.id));
    if (hasRemoval) {
      return null;
    }

    for (const section of afterSections) {
      const previous = beforeMap.get(section.id);
      if (previous && !sectionsEqual(previous, section)) {
        return section.id;
      }
    }

    return null;
  };

  const resolveHistoryHighlightTargetId = (beforePage: Page, afterPage: Page): string | null => {
    // Normalize so incidental fields (rowPosition, uiKey, block position) don't steal the highlight.
    // Strip site-owned slot fields so a stripped snapshot vs a live page doesn't
    // falsely register as a header/footer diff.
    const normalizedBefore = stripSiteOwnedFields(normalizePage(beforePage));
    const normalizedAfter = stripSiteOwnedFields(normalizePage(afterPage));

    if (
      !slotValuesEqual(normalizedBefore.slots?.header, normalizedAfter.slots?.header) ||
      !slotValuesEqual(
        normalizedBefore.metadata?.slots?.header,
        normalizedAfter.metadata?.slots?.header,
      )
    ) {
      return PAGE_EDITOR_HISTORY_TARGET_IDS.header;
    }

    if (
      !slotValuesEqual(normalizedBefore.slots?.footer, normalizedAfter.slots?.footer) ||
      !slotValuesEqual(
        normalizedBefore.metadata?.slots?.footer,
        normalizedAfter.metadata?.slots?.footer,
      )
    ) {
      return PAGE_EDITOR_HISTORY_TARGET_IDS.footer;
    }

    return resolveHistoryHighlightSectionId(
      getMainSections(normalizedBefore),
      getMainSections(normalizedAfter),
    );
  };

  const resolveHistoryRetainSectionId = (
    currentPage: Page,
    restoredPage: Page,
    recordedTargetId: string | null,
  ): string | null => {
    if (recordedTargetId) {
      return null;
    }

    const highlightTargetId =
      recordedTargetId ?? resolveHistoryHighlightTargetId(currentPage, restoredPage);
    if (
      highlightTargetId &&
      highlightTargetId !== PAGE_EDITOR_HISTORY_TARGET_IDS.header &&
      highlightTargetId !== PAGE_EDITOR_HISTORY_TARGET_IDS.footer
    ) {
      return highlightTargetId;
    }

    return resolveHistoryHighlightSectionId(
      getMainSections(currentPage),
      getMainSections(restoredPage),
    );
  };

  // Schedule the recorded/highlighted history target after undo/redo restores the
  // snapshot so both regular sections and header/footer slots are brought back
  // into view.
  const resolveHistoryScrollTargetId = (
    currentPage: Page,
    restoredPage: Page,
    recordedTargetId: string | null,
  ): string | null => {
    return recordedTargetId ?? resolveHistoryHighlightTargetId(currentPage, restoredPage);
  };

  // Autosave can remap persisted ids after draft creation. Cache those remaps so
  // later incoming payloads can still target the current draft section/block ids.
  const resolveIncomingSectionId = (sectionId: string): string =>
    resolveRemappedId(sectionId, get().incomingSectionIdMappings);

  const resolveIncomingBlockId = (blockId: string): string =>
    resolveRemappedId(blockId, get().incomingBlockIdMappings);

  const remapIncomingBlocks = (blocks: PageBlock[]): PageBlock[] => {
    const blockMappings = get().incomingBlockIdMappings;
    if (Object.keys(blockMappings).length === 0) {
      return blocks;
    }

    let nextBlocks: PageBlock[] | null = null;

    for (let index = 0; index < blocks.length; index += 1) {
      const block = blocks[index];
      const resolvedId = resolveRemappedId(block.id, blockMappings);
      const resolvedParentId = remapParentBlockId(block.parentBlockId, blockMappings);

      if (resolvedId === block.id && resolvedParentId === block.parentBlockId) {
        if (nextBlocks) {
          nextBlocks.push(block);
        }
        continue;
      }

      if (!nextBlocks) {
        nextBlocks = blocks.slice(0, index);
      }

      nextBlocks.push({
        ...block,
        id: resolvedId,
        parentBlockId: resolvedParentId,
      });
    }

    return nextBlocks ?? blocks;
  };

  /**
   * Computes the diff between two section lists and dispatches granular autosave
   * actions (add/delete/update/reorder) so the middleware can sync changes to the
   * backend. Used by undo/redo instead of a single PAGE_UPDATE, which the
   * autosave middleware cannot resolve into section-level operations.
   */
  const getChangedBlockUpdates = (
    previousBlock: PageBlock,
    nextBlock: PageBlock,
  ): Partial<PageBlock> => {
    const updates: Partial<PageBlock> = {};

    if (previousBlock.blockType !== nextBlock.blockType) {
      updates.blockType = nextBlock.blockType;
    }

    // TODO: This assumes stable object key order. Revisit if props churn causes
    // noisy autosave updates without semantic changes.
    if (JSON.stringify(previousBlock.props) !== JSON.stringify(nextBlock.props)) {
      updates.props = withRemovalsAsNull(previousBlock.props, nextBlock.props);
    }

    const previousParentId = previousBlock.parentBlockId ?? null;
    const nextParentId = nextBlock.parentBlockId ?? null;
    if (previousParentId !== nextParentId) {
      updates.parentBlockId = nextParentId;
    }

    if (previousBlock.columnSpan !== nextBlock.columnSpan) {
      updates.columnSpan = nextBlock.columnSpan;
    }

    if (previousBlock.position !== nextBlock.position) {
      updates.position = nextBlock.position;
    }

    if (previousBlock.visible !== nextBlock.visible) {
      updates.visible = nextBlock.visible;
    }

    return updates;
  };

  const dispatchSnapshotDiff = (
    beforeSections: PageSection[],
    afterSections: PageSection[],
  ): void => {
    const beforeIds = new Set(beforeSections.map((s) => s.id));
    const afterIds = new Set(afterSections.map((s) => s.id));
    const beforeMap = new Map(beforeSections.map((s) => [s.id, s]));

    // Sections removed (in before but not in after)
    for (const id of beforeIds) {
      if (!afterIds.has(id)) {
        handlePageEditorAction({
          type: PageEditorActionType.PAGE_SECTION_DELETE,
          payload: { sectionId: id },
        });
      }
    }

    // Sections added (in after but not in before)
    for (const section of afterSections) {
      if (!beforeIds.has(section.id)) {
        handlePageEditorAction({
          type: PageEditorActionType.PAGE_SECTION_ADD,
          payload: { sectionId: section.id, section },
        });
      }
    }

    // Sections that exist in both — check for content changes
    const sharedIds = new Set<string>();
    for (const section of afterSections) {
      if (!beforeIds.has(section.id)) continue;
      sharedIds.add(section.id);
      const before = beforeMap.get(section.id);
      if (!before) continue;

      const updates: Partial<PageSection> = {};
      // TODO: This assumes stable object key order. Revisit if section props
      // churn causes noisy autosave updates without semantic changes.
      if (JSON.stringify(before.props) !== JSON.stringify(section.props)) {
        updates.props = withRemovalsAsNull(before.props, section.props);
      }
      if (before.visible !== section.visible) {
        updates.visible = section.visible;
      }
      if (before.variant !== section.variant) {
        updates.variant = section.variant;
      }

      if (Object.keys(updates).length > 0) {
        handlePageEditorAction({
          type: PageEditorActionType.PAGE_SECTION_UPDATE,
          payload: { sectionId: section.id, updates },
        });
      }

      dispatchBlockDiff(section.id, before.blocks || [], section.blocks || []);
    }

    // Only dispatch reorder if the relative order of shared sections changed,
    // not just because rowPositions shifted due to adds/deletes.
    const beforeOrder = beforeSections.filter((s) => sharedIds.has(s.id)).map((s) => s.id);
    const afterOrder = afterSections.filter((s) => sharedIds.has(s.id)).map((s) => s.id);

    if (beforeOrder.some((id, i) => id !== afterOrder[i])) {
      handlePageEditorAction({
        type: PageEditorActionType.PAGE_SECTION_REORDER,
        payload: { sectionIds: afterOrder },
      });
    }
  };

  const dispatchBlockDiff = (
    sectionId: string,
    beforeBlocks: PageBlock[],
    afterBlocks: PageBlock[],
  ): void => {
    const beforeIds = new Set(beforeBlocks.map((block) => block.id));
    const afterIds = new Set(afterBlocks.map((block) => block.id));
    const beforeMap = new Map(beforeBlocks.map((block) => [block.id, block]));

    for (const blockId of beforeIds) {
      if (!afterIds.has(blockId)) {
        handlePageEditorAction({
          type: PageEditorActionType.PAGE_BLOCK_DELETE,
          payload: { sectionId, blockId },
        });
      }
    }

    for (const block of afterBlocks) {
      if (!beforeIds.has(block.id)) {
        handlePageEditorAction({
          type: PageEditorActionType.PAGE_BLOCK_ADD,
          payload: { sectionId, blockId: block.id, block },
        });
        continue;
      }

      const previousBlock = beforeMap.get(block.id);
      if (!previousBlock) {
        continue;
      }

      const updates = getChangedBlockUpdates(previousBlock, block);
      if (Object.keys(updates).length > 0) {
        handlePageEditorAction({
          type: PageEditorActionType.PAGE_BLOCK_UPDATE,
          payload: { sectionId, blockId: block.id, updates },
        });
      }
    }
  };

  const dispatchSnapshotMetadataDiff = (beforePage: Page | null, afterPage: Page | null): void => {
    if (!afterPage) return;
    if (JSON.stringify(beforePage?.metadata) === JSON.stringify(afterPage.metadata)) return;
    handlePageEditorAction({
      type: PageEditorActionType.PAGE_UPDATE,
      payload: { metadata: afterPage.metadata },
    });
  };

  // While a group is open, withHistory defers its snapshot so one user action undoes as one step.
  let historyGroupDepth = 0;
  let historyGroupSnapshot: Page | null = null;
  let historyGroupChanged = false;
  let historyGroupCallbacks: Array<() => void> = [];

  const commitHistoryEntry = (snapshot: Page | null, onSuccess: (() => void) | undefined): void => {
    if (!snapshot) {
      return;
    }
    const afterPage = get().getPageForAutosave();
    // Skip the undo entry for no-op edits, but still autosave — accepting a previewed proposal nets out unchanged.
    if (!afterPage || !pagesEquivalent(snapshot, afterPage)) {
      const targetId = afterPage ? resolveHistoryHighlightTargetId(snapshot, afterPage) : null;
      commitHistorySnapshot(snapshot, targetId);
    }
    onSuccess?.();
  };

  /**
   * Wraps an action function to automatically handle history snapshots.
   * Captures snapshot before execution and commits it after if the action succeeded.
   */
  const withHistory = ({
    action,
    onSuccess,
  }: {
    action: () => boolean;
    onSuccess?: () => void;
  }): void => {
    if (historyGroupDepth > 0) {
      if (action()) {
        historyGroupChanged = true;
        // Every grouped action's autosave dispatch has to run, not just the first one's.
        if (onSuccess) historyGroupCallbacks.push(onSuccess);
      }
      return;
    }

    const snapshot = prepareHistorySnapshot();
    if (action() && snapshot) {
      commitHistoryEntry(snapshot, onSuccess);
    }
  };

  const insertSection = (section: PageSection, insertRowPosition?: number) => {
    const state = get();
    if (!state.page) {
      return false;
    }

    const existingSections = getSortedSections(getMainSections(state.page));
    const targetIndex =
      insertRowPosition !== undefined
        ? Math.max(0, Math.min(existingSections.length, insertRowPosition))
        : existingSections.length;

    const sectionToInsert: PageSection = {
      ...section,
      rowPosition: targetIndex,
    };

    const updatedSections = [...existingSections];
    updatedSections.splice(targetIndex, 0, sectionToInsert);

    const renumberedSections = updatedSections.map((currentSection, index) => ({
      ...currentSection,
      rowPosition: index,
    }));

    set((currentState) => {
      if (!currentState.page) {
        return currentState;
      }
      return {
        ...currentState,
        page: updateMainSections(currentState.page, renumberedSections),
      };
    });
    return true;
  };

  const prepareSectionForInsert = async (
    section: Omit<PageSection, 'id' | 'rowPosition'> & { id?: string },
    insertRowPosition?: number,
    options?: AddSectionOptions,
  ): Promise<PageSection | null> => {
    const state = get();
    const pageId = state.page?.id;

    let propsToUse = section.props ?? {};
    let blocksToUse = section.blocks;
    if (pageId && section.sectionTemplateCode && !options?.skipDefaultHydration) {
      try {
        const resolved = await resolveSectionPropsForInsert(
          section.sectionTemplateCode,
          propsToUse,
          {
            pageId,
          },
        );
        propsToUse = resolved.props ?? propsToUse;
        const defaultBlocks = resolved.defaultBlocks;
        if (!blocksToUse && Array.isArray(defaultBlocks) && defaultBlocks.length > 0) {
          blocksToUse = await hydrateInsertedDataWidgetBlocks(
            normalizeDefaultBlocks(defaultBlocks),
            pageId,
            section.sectionTemplateCode,
          );
        }
      } catch (error) {
        console.error(
          `Failed to resolve props for section template ${section.sectionTemplateCode}:`,
          error,
        );
      }
    }

    if (options?.hydrateDataWidgets && blocksToUse && pageId) {
      try {
        blocksToUse = await hydrateInsertedDataWidgetBlocks(
          blocksToUse,
          pageId,
          section.sectionTemplateCode,
        );
      } catch (error) {
        console.error('Failed to hydrate data widget blocks for saved section:', error);
      }
    }

    const generatedSectionId = section.id || uuidv7();

    return {
      ...section,
      props: propsToUse,
      blocks: blocksToUse,
      id: generatedSectionId,
      uiKey: generatedSectionId,
      rowPosition: insertRowPosition ?? 0,
      // Don't reuse the source section's public DOM id on duplicate/insert.
      stableId: undefined,
    } as PageSection;
  };

  const removeSectionById = (sectionId: string) => {
    const state = get();
    if (!state.page) {
      return false;
    }

    const sections = getMainSections(state.page);
    const section = sections.find((s) => s.id === sectionId);
    if (!section || section.permissions?.manage === false) {
      return false;
    }

    const updatedSections = sections
      .filter((s) => s.id !== sectionId)
      .map((currentSection, index) => ({ ...currentSection, rowPosition: index }));

    set((currentState) => {
      if (!currentState.page) {
        return currentState;
      }
      return {
        ...currentState,
        page: updateMainSections(currentState.page, updatedSections),
      };
    });
    return true;
  };

  const replaceSectionById = (
    sectionId: string,
    replacement: Omit<PageSection, 'id' | 'rowPosition'> &
      Partial<Pick<PageSection, 'rowPosition'>>,
  ): PageSection | null => {
    const state = get();
    if (!state.page) {
      return null;
    }

    const sections = getSortedSections(getMainSections(state.page));
    const currentIndex = sections.findIndex((section) => section.id === sectionId);
    if (currentIndex === -1) {
      return null;
    }

    const existingSection = sections[currentIndex];
    if (existingSection.permissions?.manage === false) {
      return null;
    }

    const replacementSectionId = uuidv7();
    const nextSection: PageSection = {
      ...replacement,
      id: replacementSectionId,
      uiKey: replacementSectionId,
      rowPosition: existingSection.rowPosition,
      props: replacement.props ?? {},
      visible:
        typeof replacement.visible === 'boolean' ? replacement.visible : existingSection.visible,
    } as PageSection;

    const updatedSections = [...sections];
    updatedSections[currentIndex] = nextSection;

    set((currentState) => {
      if (!currentState.page) {
        return currentState;
      }

      return {
        ...currentState,
        page: updateMainSections(currentState.page, updatedSections),
      };
    });

    return nextSection;
  };

  /**
   * Merges section updates, handling nested objects (e.g., props).
   * Only merges one level deep for nested objects - other fields are replaced.
   * This ensures partial updates to nested objects don't overwrite other properties.
   */
  const mergeSectionUpdates = (
    section: PageSection,
    updates: Partial<PageSection>,
  ): PageSection => {
    const result = { ...section, ...updates };

    // If both have props objects, merge them to preserve other props
    if (updates.props && typeof updates.props === 'object' && !Array.isArray(updates.props)) {
      result.props = {
        ...(section.props || {}),
        ...updates.props,
      };
    }

    return result;
  };

  const updateSectionById = (sectionId: string, updates: Partial<PageSection>) => {
    const state = get();
    if (!state.page) {
      return false;
    }

    const sections = getMainSections(state.page);
    const section = sections.find((s) => s.id === sectionId);
    if (!section) {
      return false;
    }

    const updatedSection = mergeSectionUpdates(section, updates);

    // Return false (no-op) if the section data hasn't actually changed, to avoid
    // triggering unnecessary autosave and incorrectly marking the page as changed.
    const didChange = (Object.keys(updates) as (keyof PageSection)[]).some((key) => {
      if (key === 'props') {
        return JSON.stringify(section.props) !== JSON.stringify(updatedSection.props);
      }
      return section[key] !== updatedSection[key];
    });
    if (!didChange) {
      return false;
    }

    const nextSections = sections.map((s) => (s.id === sectionId ? updatedSection : s));

    set((currentState) => {
      if (!currentState.page) {
        return currentState;
      }
      return {
        ...currentState,
        page: updateMainSections(currentState.page, nextSections),
      };
    });
    return true;
  };

  const replaceSectionPreviewById = (sectionId: string, snapshot: Partial<PageSection>) => {
    const state = get();
    if (!state.page) {
      return false;
    }

    const sections = getMainSections(state.page);
    const section = sections.find((currentSection) => currentSection.id === sectionId);
    if (!section) {
      return false;
    }

    const snapshotRecord = snapshot as Record<string, unknown>;
    const hasProps = 'props' in snapshotRecord;
    const hasBlocks = 'blocks' in snapshotRecord;
    const hasVariant = 'variant' in snapshotRecord;
    const hasVisible = 'visible' in snapshotRecord;
    const hasSectionTemplateCode = 'sectionTemplateCode' in snapshotRecord;

    const nextSection: PageSection = {
      ...section,
      ...(hasSectionTemplateCode
        ? { sectionTemplateCode: snapshot.sectionTemplateCode ?? '' }
        : {}),
      ...(hasVariant ? { variant: snapshot.variant } : {}),
      ...(hasVisible
        ? { visible: typeof snapshot.visible === 'boolean' ? snapshot.visible : section.visible }
        : {}),
      ...(hasProps ? { props: (snapshot.props as Record<string, unknown> | undefined) ?? {} } : {}),
      ...(hasBlocks ? { blocks: snapshot.blocks } : {}),
    };

    if (JSON.stringify(section) === JSON.stringify(nextSection)) {
      return false;
    }

    const nextSections = sections.map((currentSection) =>
      currentSection.id === sectionId ? nextSection : currentSection,
    );

    set((currentState) => {
      if (!currentState.page) {
        return currentState;
      }
      return {
        ...currentState,
        page: updateMainSections(currentState.page, nextSections),
      };
    });
    return true;
  };

  const updateSectionBlocks = ({
    sectionId,
    transform,
  }: {
    sectionId: string;
    transform: (blocks: PageBlock[]) => PageBlock[];
  }): boolean => {
    const state = get();
    if (!state.page) {
      return false;
    }

    const sections = getMainSections(state.page);
    const section = sections.find((currentSection) => currentSection.id === sectionId);
    if (!section) {
      return false;
    }

    const currentBlocks = section.blocks || [];
    const nextBlocks = transform(currentBlocks);
    if (nextBlocks === currentBlocks) {
      return false;
    }

    const nextSections = sections.map((currentSection) =>
      currentSection.id === sectionId ? { ...currentSection, blocks: nextBlocks } : currentSection,
    );

    set((currentState) => {
      if (!currentState.page) {
        return currentState;
      }

      return {
        ...currentState,
        page: updateMainSections(currentState.page, nextSections),
      };
    });

    return true;
  };

  const setSectionBlocks = (sectionId: string, nextBlocks: PageBlock[]): boolean => {
    const state = get();
    if (!state.page) {
      return false;
    }

    const sections = getMainSections(state.page);
    const section = sections.find((currentSection) => currentSection.id === sectionId);
    if (!section) {
      return false;
    }

    const currentBlocks = section.blocks || [];
    if (nextBlocks === currentBlocks) {
      return false;
    }

    const nextSections = sections.map((currentSection) =>
      currentSection.id === sectionId ? { ...currentSection, blocks: nextBlocks } : currentSection,
    );

    set((currentState) => {
      if (!currentState.page) {
        return currentState;
      }

      return {
        ...currentState,
        page: updateMainSections(currentState.page, nextSections),
      };
    });

    return true;
  };

  const restoreFloatLayoutPreviewBaseline = (sectionId: string, blockId: string): boolean => {
    const key = getFloatLayoutPreviewKey(sectionId, blockId);
    const baseline = floatLayoutPreviewBaselines.get(key);
    if (!baseline) {
      return false;
    }

    floatLayoutPreviewBaselines.delete(key);
    return setSectionBlocks(sectionId, baseline.blocks);
  };

  const getFloatLayoutPreviewBaselineForSection = (
    sectionId: string,
  ): FloatLayoutPreviewBaseline | undefined =>
    Array.from(floatLayoutPreviewBaselines.values()).find(
      (baseline) => baseline.sectionId === sectionId,
    );

  const restoreFloatLayoutPreviewForSection = (sectionId: string): boolean => {
    const baseline = getFloatLayoutPreviewBaselineForSection(sectionId);
    return baseline
      ? restoreFloatLayoutPreviewBaseline(baseline.sectionId, baseline.blockId)
      : false;
  };

  const addBlockToSection = (
    sectionId: string,
    block: Omit<PageBlock, 'id' | 'uiKey'> & { id?: string; uiKey?: string },
  ) => {
    const blockId = block.id || uuidv7();
    const newBlock: PageBlock = {
      ...block,
      id: blockId,
      uiKey: block.uiKey || getBlockUiKey({ id: blockId }),
    };

    const didUpdate = updateSectionBlocks({
      sectionId,
      transform: (blocks) =>
        invalidateFloatRestorePositionsForChangedSiblings(blocks, [...blocks, newBlock]),
    });

    return didUpdate ? newBlock.id : null;
  };

  const updateBlockById = (
    sectionId: string,
    blockId: string,
    updates: Partial<PageBlock>,
  ): boolean => {
    return updateSectionBlocks({
      sectionId,
      transform: (blocks) => {
        const index = blocks.findIndex((block) => block.id === blockId);
        if (index === -1) {
          return blocks;
        }

        const nextBlocks = [...blocks];
        nextBlocks[index] = {
          ...nextBlocks[index],
          ...updates,
          id: nextBlocks[index].id,
          uiKey: nextBlocks[index].uiKey,
        };
        return nextBlocks;
      },
    });
  };

  const deleteBlockById = (
    sectionId: string,
    blockId: string,
    preserveEmptyParentIds: ReadonlySet<string> = new Set<string>(),
  ): boolean => {
    const section = getMainSections(get().page).find(
      (currentSection) => currentSection.id === sectionId,
    );
    if (!section) {
      return false;
    }

    return updateSectionBlocks({
      sectionId,
      transform: (blocks) => {
        const cleanedBlocks = removeBlockSubtreeAndCleanup(blocks, blockId, preserveEmptyParentIds);
        if (isSpecialPageLayoutSection(section)) {
          return invalidateFloatRestorePositionsForChangedSiblings(blocks, cleanedBlocks);
        }

        const nextBlocks = normalizeBlocksStructure(
          cleanedBlocks,
          resolveBlockStackLayout(section.props).direction,
        );
        return invalidateFloatRestorePositionsForChangedSiblings(blocks, nextBlocks);
      },
    });
  };

  const moveSectionById = (sectionId: string, direction: 'up' | 'down'): boolean => {
    const state = get();
    if (!state.page) {
      return false;
    }

    const sections = getSortedSections(getMainSections(state.page));
    const currentIndex = sections.findIndex((s) => s.id === sectionId);
    const section = sections[currentIndex];

    if (!section) {
      return false;
    }

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (targetIndex < 0 || targetIndex >= sections.length) {
      return false;
    }

    const updatedSections = [...sections];
    [updatedSections[currentIndex], updatedSections[targetIndex]] = [
      updatedSections[targetIndex],
      updatedSections[currentIndex],
    ];

    const renumberedSections = updatedSections.map((s, index) => ({
      ...s,
      rowPosition: index,
    }));

    set((currentState) => {
      if (!currentState.page) {
        return currentState;
      }
      return {
        ...currentState,
        page: updateMainSections(currentState.page, renumberedSections),
      };
    });
    return true;
  };

  /**
   * Core state update function for reordering sections.
   * Takes a complete list of section IDs in the desired order and updates positions.
   * This is the single source of truth for section reordering state updates.
   */
  const reorderSectionsByIds = (sectionIds: string[]): boolean => {
    const state = get();
    if (!state.page) {
      return false;
    }

    const sections = getMainSections(state.page);
    const sectionMap = new Map(sections.map((s) => [s.id, s]));
    const uniqueSectionIds = new Set(sectionIds);
    if (sections.length !== sectionIds.length || uniqueSectionIds.size !== sections.length) {
      return false;
    }
    if (![...uniqueSectionIds].every((id) => sectionMap.has(id))) {
      return false;
    }

    const reorderedSections = sectionIds
      .map((id, index) => {
        const section = sectionMap.get(id);
        return section ? { ...section, rowPosition: index } : null;
      })
      .filter((s): s is PageSection => s !== null);

    set((currentState) => {
      if (!currentState.page) {
        return currentState;
      }
      return {
        ...currentState,
        page: updateMainSections(currentState.page, reorderedSections),
      };
    });
    return true;
  };

  return {
    page: null,
    isGeneratingSeo: false,
    previewNavigationPage: null,
    historyHighlightSectionId: null,
    historyHighlightVersion: 0,
    historyPast: persistedPageEditorHistory.historyPast,
    historyFuture: persistedPageEditorHistory.historyFuture,
    historyPastTargets: persistedPageEditorHistory.historyPastTargets,
    historyFutureTargets: persistedPageEditorHistory.historyFutureTargets,
    historyOrder: persistedPageEditorHistory.historyOrder,
    historyOrderFuture: persistedPageEditorHistory.historyOrderFuture,
    siteHistoryAdapter: null,
    pendingCrossPageHistoryAction: null,
    pendingCrossPageHistoryPageId: null,
    pendingUndoConfirmation: false,
    pendingUndoProposalIds: [],
    editingSlot: null,
    incomingSectionIdMappings: {},
    incomingBlockIdMappings: {},
    historyAccountId: null,

    registerSiteHistoryAdapter: (adapter) => set({ siteHistoryAdapter: adapter }),

    initHistoryScope: (accountId) => {
      if (!isEditorWindow) return;
      const previous = get().historyAccountId;
      if (previous === accountId) return;

      purgeOtherEditorHistoryAccounts(accountId);
      const { page: persisted } = readPersistedEditorHistory(accountId);

      withApplyingPersistedEditorHistory(() => {
        set((state) => ({
          ...state,
          historyAccountId: accountId,
          historyPast: persisted.historyPast,
          historyFuture: persisted.historyFuture,
          historyPastTargets: persisted.historyPastTargets,
          historyFutureTargets: persisted.historyFutureTargets,
          historyOrder: persisted.historyOrder,
          historyOrderFuture: persisted.historyOrderFuture,
          pendingCrossPageHistoryAction: null,
          pendingCrossPageHistoryPageId: null,
          pendingUndoConfirmation: false,
          pendingUndoProposalIds: [],
        }));
      });
    },

    recordSiteHistoryEntry: () => {
      if (!isEditorWindow) return;
      set((state) => ({
        ...state,
        historyOrder: state.historyOrder.concat('site').slice(-HISTORY_LIMIT),
        historyOrderFuture: [],
        historyFuture: [],
        historyFutureTargets: [],
      }));
    },

    setPage: (page, options) => {
      const shouldResetHistory = options?.resetHistory ?? false;
      const currentPage = get().page;
      const preservesLiveMainSections =
        page !== null &&
        currentPage !== null &&
        page.id === currentPage.id &&
        getMainSections(page) === getMainSections(currentPage);
      if (!preservesLiveMainSections || shouldResetHistory) {
        floatLayoutPreviewBaselines.clear();
      }
      set((state) => {
        const baseState = shouldResetHistory ? clearPageHistoryState(state) : state;

        if (!page) {
          return {
            ...baseState,
            page: null,
            previewNavigationPage: null,
            editingSlot: null,
            incomingSectionIdMappings: {},
            incomingBlockIdMappings: {},
          };
        }

        const normalizedPage = normalizePage(page);
        const syncPreview = shouldSyncPreviewNavigation(
          baseState.previewNavigationPage,
          normalizedPage.id,
        );

        return {
          ...baseState,
          page: normalizedPage,
          previewNavigationPage: syncPreview
            ? buildPreviewNavigation(normalizedPage)
            : baseState.previewNavigationPage,
          incomingSectionIdMappings: shouldResetHistory ? {} : baseState.incomingSectionIdMappings,
          incomingBlockIdMappings: shouldResetHistory ? {} : baseState.incomingBlockIdMappings,
        };
      });

      // Sync metadata to pages store for sidebar updates (local update, no API call)
      if (page) {
        const normalizedPage = normalizePage(page);
        const pagesStore = usePagesStore.getState();
        pagesStore.updatePage(normalizedPage.id, {
          title: normalizedPage.title,
          navigationTitle: normalizedPage.navigationTitle,
          path: normalizedPage.path,
          isPublished: normalizedPage.isPublished,
          isNav: normalizedPage.isNav,
          hasUnpublishedChanges: normalizedPage.hasUnpublishedChanges,
          scheduledPublishAt: normalizedPage.scheduledPublishAt,
        });

        useNestedPageViewStore.getState().updatePage(normalizedPage);

        // Sync to nested page view store for child pages (blogs, case results)
        const indexPageType = CHILD_TO_INDEX_TYPE[normalizedPage.pageType];
        if (indexPageType) {
          const nestedViewStore = useNestedPageViewStore.getState();
          const { activeNestedView } = nestedViewStore;

          if (activeNestedView?.isIndexPage && activeNestedView.pageType === indexPageType) {
            const pageExists = activeNestedView.children.some(
              (child) => child.id === normalizedPage.id,
            );

            if (pageExists) {
              nestedViewStore.updatePage(normalizedPage);
            } else {
              nestedViewStore.addPage(normalizedPage, indexPageType as PageType);
            }
          }
        }

        const { pendingCrossPageHistoryAction, pendingCrossPageHistoryPageId } = get();

        if (pendingCrossPageHistoryAction && pendingCrossPageHistoryPageId === normalizedPage.id) {
          set({
            pendingCrossPageHistoryAction: null,
            pendingCrossPageHistoryPageId: null,
          });
          queueMicrotask(() => {
            get()[pendingCrossPageHistoryAction]();
          });
        }
      }
    },

    getPageForAutosave: () => {
      const page = get().page;
      if (!page || floatLayoutPreviewBaselines.size === 0) {
        return page;
      }

      const baselineBlocksBySection = new Map<string, PageBlock[]>();
      for (const baseline of floatLayoutPreviewBaselines.values()) {
        if (!baselineBlocksBySection.has(baseline.sectionId)) {
          baselineBlocksBySection.set(baseline.sectionId, baseline.blocks);
        }
      }

      const sections = getMainSections(page);
      const committedSections = sections.map((section) => {
        const baselineBlocks = baselineBlocksBySection.get(section.id);
        return baselineBlocks ? { ...section, blocks: baselineBlocks } : section;
      });
      return updateMainSections(page, committedSections);
    },

    // Internal path for autosave ID remaps. Avoids metadata sync side effects from setPage.
    setPageForAutosaveRemap: (sectionsMapping, blocksMapping = {}) => {
      const remappedBaselines = Array.from(floatLayoutPreviewBaselines.values()).map(
        (baseline) => ({
          ...baseline,
          sectionId: resolveRemappedId(baseline.sectionId, sectionsMapping),
          blockId: resolveRemappedId(baseline.blockId, blocksMapping),
          blocks: baseline.blocks.map((block) => ({
            ...block,
            id: resolveRemappedId(block.id, blocksMapping),
            parentBlockId: remapParentBlockId(block.parentBlockId, blocksMapping),
          })),
        }),
      );
      floatLayoutPreviewBaselines.clear();
      for (const baseline of remappedBaselines) {
        floatLayoutPreviewBaselines.set(
          getFloatLayoutPreviewKey(baseline.sectionId, baseline.blockId),
          baseline,
        );
      }

      set((state) => {
        if (!state.page) {
          return state;
        }

        const { hasRemappedIds, page: remappedPage } = remapPageContentIds({
          page: state.page,
          sectionsMapping,
          blocksMapping,
        });
        if (!hasRemappedIds) {
          return state;
        }

        const remapHistorySnapshot = (snapshot: Page): Page =>
          remapPageContentIds({ page: snapshot, sectionsMapping, blocksMapping }).page;
        const remapHistoryTarget = (target: string | null): string | null =>
          target && sectionsMapping[target] ? sectionsMapping[target] : target;
        const normalizedPage = normalizePage({
          ...remappedPage,
          hasUnpublishedChanges: true,
        });
        const syncPreview = shouldSyncPreviewNavigation(
          state.previewNavigationPage,
          normalizedPage.id,
        );

        return {
          ...state,
          page: normalizedPage,
          previewNavigationPage: syncPreview
            ? buildPreviewNavigation(normalizedPage)
            : state.previewNavigationPage,
          incomingSectionIdMappings: mergeIdMappings(
            state.incomingSectionIdMappings,
            sectionsMapping,
          ),
          incomingBlockIdMappings: mergeIdMappings(state.incomingBlockIdMappings, blocksMapping),
          historyPast: state.historyPast.map(remapHistorySnapshot),
          historyFuture: state.historyFuture.map(remapHistorySnapshot),
          historyPastTargets: state.historyPastTargets.map(remapHistoryTarget),
          historyFutureTargets: state.historyFutureTargets.map(remapHistoryTarget),
        };
      });
    },

    setEditingSlot: (slot) => set({ editingSlot: slot }),

    setSlotVisibility: (slot, visible) => {
      withHistory({
        action: () => {
          const state = get();
          if (!state.page) return false;
          const currentVisible = state.page.metadata?.slots?.[slot]?.visible ?? true;
          if (currentVisible === visible) return false;
          set((currentState) => {
            if (!currentState.page) return currentState;
            const metadata = currentState.page.metadata ?? {};
            return {
              ...currentState,
              page: {
                ...currentState.page,
                metadata: {
                  ...metadata,
                  slots: {
                    ...metadata.slots,
                    [slot]: { ...(metadata.slots?.[slot] ?? {}), visible },
                  },
                },
              },
            };
          });
          return true;
        },
        onSuccess: () => {
          handlePageEditorAction({
            type: PageEditorActionType.PAGE_UPDATE,
            payload: { metadata: get().page?.metadata },
          });
        },
      });
    },

    addSection: async (section, insertRowPosition, options) => {
      const newSection = await prepareSectionForInsert(section, insertRowPosition, options);
      if (!newSection) {
        return null;
      }

      let didInsert = false;
      withHistory({
        action: () => {
          didInsert = insertSection(newSection, insertRowPosition);
          return didInsert;
        },
        onSuccess: () => {
          // Get the actual section with computed row from store
          // insertSection renumbers all sections sequentially, so we need the final row
          const currentState = get();
          const sections = getMainSections(currentState.page);
          const actualSection = sections.find((s) => s.id === newSection.id) ?? newSection;

          // Use actualSection which has the correct computed row after renumbering
          handlePageEditorAction({
            type: PageEditorActionType.PAGE_SECTION_ADD,
            payload: {
              sectionId: actualSection.id,
              section: actualSection,
              insertRowPosition: actualSection.rowPosition, // Use computed row, not original insertRowPosition
            },
          });
        },
      });

      return didInsert ? newSection.id : null;
    },

    previewAddSection: async (section, insertRowPosition, options) => {
      const newSection = await prepareSectionForInsert(section, insertRowPosition, options);
      if (!newSection) {
        return null;
      }

      const didInsert = insertSection(newSection, insertRowPosition);
      return didInsert ? newSection.id : null;
    },

    addBlock: (sectionId, block) => {
      let newBlockId: string | null = null;
      let beforeBlocks: PageBlock[] = [];
      const resolvedSectionId = resolveIncomingSectionId(sectionId);
      restoreFloatLayoutPreviewForSection(resolvedSectionId);

      withHistory({
        action: () => {
          const section = getMainSections(get().page).find(
            (currentSection) => currentSection.id === resolvedSectionId,
          );
          beforeBlocks = section?.blocks || [];
          newBlockId = addBlockToSection(resolvedSectionId, block);
          return newBlockId !== null;
        },
        onSuccess: () => {
          if (!newBlockId) {
            return;
          }

          const section = getMainSections(get().page).find(
            (currentSection) => currentSection.id === resolvedSectionId,
          );
          const afterBlocks = section?.blocks || [];
          if (!afterBlocks.some((currentBlock) => currentBlock.id === newBlockId)) {
            return;
          }
          dispatchBlockDiff(resolvedSectionId, beforeBlocks, afterBlocks);
        },
      });

      return newBlockId;
    },

    updateBlock: (sectionId, blockId, updates) => {
      const resolvedSectionId = resolveIncomingSectionId(sectionId);
      const resolvedBlockId = resolveIncomingBlockId(blockId);
      restoreFloatLayoutPreviewForSection(resolvedSectionId);

      const section = getMainSections(get().page).find(
        (currentSection) => currentSection.id === resolvedSectionId,
      );
      const blocks = section?.blocks ?? [];
      const target = blocks.find((block) => block.id === resolvedBlockId);
      const props = updates.props;
      const needsFloatLayout =
        props !== undefined &&
        target !== undefined &&
        (hasFloatLayoutProps(props) || hasFloatLayoutProps(target.props ?? {}));

      if (section && target && props !== undefined && needsFloatLayout) {
        const otherUpdates = { ...updates };
        delete otherUpdates.props;
        const sourceBlocks =
          Object.keys(otherUpdates).length === 0
            ? blocks
            : blocks.map((block) =>
                block.id === resolvedBlockId
                  ? {
                      ...block,
                      ...otherUpdates,
                      id: block.id,
                      uiKey: block.uiKey,
                    }
                  : block,
              );
        const nextBlocks = applyBlockPropsWithFloatLayout({
          blocks: sourceBlocks,
          blockId: resolvedBlockId,
          props,
          rootDirection: resolveBlockStackLayout(section.props).direction,
        });

        withHistory({
          action: () => setSectionBlocks(resolvedSectionId, nextBlocks),
          onSuccess: () => dispatchBlockDiff(resolvedSectionId, blocks, nextBlocks),
        });
        return;
      }

      withHistory({
        action: () => updateBlockById(resolvedSectionId, resolvedBlockId, updates),
        onSuccess: () => {
          handlePageEditorAction({
            type: PageEditorActionType.PAGE_BLOCK_UPDATE,
            payload: {
              sectionId: resolvedSectionId,
              blockId: resolvedBlockId,
              updates,
            },
          });
        },
      });
    },

    previewBlockPropsWithFloatLayout: (sectionId, blockId, props) => {
      const resolvedSectionId = resolveIncomingSectionId(sectionId);
      const resolvedBlockId = resolveIncomingBlockId(blockId);
      const existingSectionPreview = getFloatLayoutPreviewBaselineForSection(resolvedSectionId);
      if (existingSectionPreview && existingSectionPreview.blockId !== resolvedBlockId) {
        restoreFloatLayoutPreviewBaseline(
          existingSectionPreview.sectionId,
          existingSectionPreview.blockId,
        );
      }

      const section = getMainSections(get().page).find(
        (currentSection) => currentSection.id === resolvedSectionId,
      );
      if (!section) {
        return;
      }

      const blocks = section.blocks || [];
      const target = blocks.find((block) => block.id === resolvedBlockId);
      if (!target) {
        return;
      }

      const previewKey = getFloatLayoutPreviewKey(resolvedSectionId, resolvedBlockId);
      const baseline = floatLayoutPreviewBaselines.get(previewKey);
      const sourceBlocks = baseline?.blocks ?? blocks;
      const sourceTarget = sourceBlocks.find((block) => block.id === resolvedBlockId);
      if (!sourceTarget) {
        return;
      }

      const nextBlocks =
        !hasFloatLayoutProps(props) && !hasFloatLayoutProps(sourceTarget.props ?? {})
          ? sourceBlocks.map((block) =>
              block.id === resolvedBlockId ? { ...block, props } : block,
            )
          : applyBlockPropsWithFloatLayout({
              blocks: sourceBlocks,
              blockId: resolvedBlockId,
              props,
              rootDirection: resolveBlockStackLayout(section.props).direction,
            });

      if (baseline && equal(nextBlocks, baseline.blocks)) {
        floatLayoutPreviewBaselines.delete(previewKey);
        setSectionBlocks(resolvedSectionId, baseline.blocks);
        return;
      }

      if (!baseline && !equal(nextBlocks, blocks)) {
        floatLayoutPreviewBaselines.set(previewKey, {
          sectionId: resolvedSectionId,
          blockId: resolvedBlockId,
          blocks,
        });
      }

      setSectionBlocks(resolvedSectionId, nextBlocks);
    },

    cancelBlockPropsWithFloatLayoutPreview: (sectionId, blockId) => {
      restoreFloatLayoutPreviewBaseline(
        resolveIncomingSectionId(sectionId),
        resolveIncomingBlockId(blockId),
      );
    },

    // Apply transient editor-only block updates without history or autosave.
    updateBlockPreview: (sectionId, blockId, updates) => {
      updateBlockById(
        resolveIncomingSectionId(sectionId),
        resolveIncomingBlockId(blockId),
        updates,
      );
    },

    batchUpdateBlocks: (sectionId, updates) => {
      const resolvedSectionId = resolveIncomingSectionId(sectionId);
      restoreFloatLayoutPreviewForSection(resolvedSectionId);

      withHistory({
        action: () => {
          let anyUpdated = false;
          for (const { blockId, updates: blockUpdates } of updates) {
            const updated = updateBlockById(
              resolvedSectionId,
              resolveIncomingBlockId(blockId),
              blockUpdates,
            );
            if (updated) anyUpdated = true;
          }
          return anyUpdated;
        },
        onSuccess: () => {
          for (const { blockId, updates: blockUpdates } of updates) {
            handlePageEditorAction({
              type: PageEditorActionType.PAGE_BLOCK_UPDATE,
              payload: {
                sectionId: resolvedSectionId,
                blockId: resolveIncomingBlockId(blockId),
                updates: blockUpdates,
              },
            });
          }
        },
      });
    },

    updateBlocks: (sectionId, blocks) => {
      const resolvedSectionId = resolveIncomingSectionId(sectionId);
      // Full-array callers derive their result from the rendered tree. If a settings preview is
      // active, restore it and ignore this stale structural result; normal pointer interaction
      // closes the settings overlay before a drag reaches this boundary.
      if (restoreFloatLayoutPreviewForSection(resolvedSectionId)) {
        return;
      }
      const resolvedBlocks = remapIncomingBlocks(blocks);
      const state = get();
      const section = getMainSections(state.page).find(
        (currentSection) => currentSection.id === resolvedSectionId,
      );
      if (!section) {
        return;
      }

      const beforeBlocks = section.blocks || [];
      const nextBlocks = invalidateFloatRestorePositionsForChangedSiblings(
        beforeBlocks,
        resolvedBlocks,
      );

      withHistory({
        action: () => setSectionBlocks(resolvedSectionId, nextBlocks),
        onSuccess: () => {
          dispatchBlockDiff(resolvedSectionId, beforeBlocks, nextBlocks);
        },
      });
    },

    deleteBlock: (sectionId, blockId, options) => {
      const resolvedSectionId = resolveIncomingSectionId(sectionId);
      const resolvedBlockId = resolveIncomingBlockId(blockId);
      restoreFloatLayoutPreviewForSection(resolvedSectionId);
      const state = get();
      const section = getMainSections(state.page).find(
        (currentSection) => currentSection.id === resolvedSectionId,
      );
      if (!section) {
        return;
      }

      const beforeBlocks = section.blocks || [];

      withHistory({
        action: () =>
          deleteBlockById(resolvedSectionId, resolvedBlockId, options?.preserveEmptyParentIds),
        onSuccess: () => {
          const afterSection = getMainSections(get().page).find(
            (currentSection) => currentSection.id === resolvedSectionId,
          );
          dispatchBlockDiff(resolvedSectionId, beforeBlocks, afterSection?.blocks || []);
        },
      });
    },

    updateSection: (sectionId, updates, options) => {
      const resolvedSectionId = resolveIncomingSectionId(sectionId);
      let specialPageBeforeBlocks: PageBlock[] | null = null;
      if (updates.blocks !== undefined) {
        restoreFloatLayoutPreviewForSection(resolvedSectionId);
        const section = getMainSections(get().page).find(
          (currentSection) => currentSection.id === resolvedSectionId,
        );
        if (section && isSpecialPageLayoutSection(section)) {
          specialPageBeforeBlocks = section.blocks || [];
        }
      }

      const dispatchAutosave = () => {
        if (specialPageBeforeBlocks) {
          const { blocks: _blocks, ...sectionUpdates } = updates;
          if (Object.keys(sectionUpdates).length > 0) {
            handlePageEditorAction({
              type: PageEditorActionType.PAGE_SECTION_UPDATE,
              payload: {
                sectionId: resolvedSectionId,
                updates: sectionUpdates,
              },
            });
          }

          const afterSection = getMainSections(get().page).find(
            (currentSection) => currentSection.id === resolvedSectionId,
          );
          dispatchBlockDiff(resolvedSectionId, specialPageBeforeBlocks, afterSection?.blocks || []);
          return;
        }

        handlePageEditorAction({
          type: PageEditorActionType.PAGE_SECTION_UPDATE,
          payload: {
            sectionId: resolvedSectionId,
            updates,
          },
        });
      };

      if (options?.skipHistory) {
        if (updateSectionById(resolvedSectionId, updates)) {
          dispatchAutosave();
        }
        return;
      }

      withHistory({
        action: () => updateSectionById(resolvedSectionId, updates),
        onSuccess: dispatchAutosave,
      });
    },

    updateSectionPreview: (sectionId, updates) => {
      updateSectionById(resolveIncomingSectionId(sectionId), updates);
    },

    replaceSectionPreview: (sectionId, snapshot) => {
      replaceSectionPreviewById(resolveIncomingSectionId(sectionId), snapshot);
    },

    // Apply externally persisted updates (e.g., webhook/image stream) without autosave/history.
    updateSectionFromServer: (sectionId, updates) => {
      const resolvedSectionId = resolveIncomingSectionId(sectionId);
      if (updates.blocks !== undefined) {
        restoreFloatLayoutPreviewForSection(resolvedSectionId);
      }
      const didUpdate = updateSectionById(resolvedSectionId, updates);
      if (didUpdate) {
        markPersistedPageContentChange();
      }
    },

    updateContentModelDraft: (sectionId, contentModelPatch) => {
      const resolvedSectionId = resolveIncomingSectionId(sectionId);
      let sectionUpdates: Partial<PageSection> | null = null;

      withHistory({
        action: () => {
          const state = get();
          if (!state.page) return false;

          const section = getMainSections(state.page).find(
            (currentSection) => currentSection.id === resolvedSectionId,
          );
          if (!section) return false;

          const existingContentModel =
            section.props?.contentModel &&
            typeof section.props.contentModel === 'object' &&
            !Array.isArray(section.props.contentModel)
              ? section.props.contentModel
              : {};
          const nextSectionContentModel = {
            ...existingContentModel,
            ...contentModelPatch,
          };
          sectionUpdates = {
            props: sectionPropsForContentModelDraft(section.props, nextSectionContentModel),
          };

          const nextPage = {
            ...state.page,
            contentModel: {
              ...(state.page.contentModel || {}),
              ...contentModelPatch,
            },
          };

          set((currentState) => ({
            ...currentState,
            page: updateMainSections(
              nextPage,
              getMainSections(nextPage).map((currentSection) =>
                currentSection.id === resolvedSectionId
                  ? mergeSectionUpdates(currentSection, sectionUpdates as Partial<PageSection>)
                  : currentSection,
              ),
            ),
          }));

          return true;
        },
        onSuccess: () => {
          if (!sectionUpdates) return;

          handlePageEditorAction({
            type: PageEditorActionType.PAGE_SECTION_UPDATE,
            payload: {
              sectionId: resolvedSectionId,
              updates: sectionUpdates,
            },
          });
        },
      });
    },

    // Mirror a content-model patch onto the reactive page-level model only; the section draft is the persisted source.
    syncPageLevelContentModel: (contentModelPatch) => {
      set((currentState) => {
        if (!currentState.page) return currentState;
        return {
          ...currentState,
          page: {
            ...currentState.page,
            contentModel: {
              ...(currentState.page.contentModel || {}),
              ...contentModelPatch,
            },
          },
        };
      });
    },

    deleteSection: (sectionId) => {
      const resolvedSectionId = resolveIncomingSectionId(sectionId);
      restoreFloatLayoutPreviewForSection(resolvedSectionId);
      withHistory({
        action: () => removeSectionById(resolvedSectionId),
        onSuccess: () => {
          handlePageEditorAction({
            type: PageEditorActionType.PAGE_SECTION_DELETE,
            payload: {
              sectionId: resolvedSectionId,
            },
          });
        },
      });
    },

    removePreviewSection: (sectionId) => {
      removeSectionById(resolveIncomingSectionId(sectionId));
    },

    replaceSection: (sectionId, section) => {
      const resolvedSectionId = resolveIncomingSectionId(sectionId);
      restoreFloatLayoutPreviewForSection(resolvedSectionId);
      let replacedSection: PageSection | null = null;

      withHistory({
        action: () => {
          replacedSection = replaceSectionById(resolvedSectionId, section);
          return replacedSection !== null;
        },
        onSuccess: () => {
          if (!replacedSection) {
            return;
          }

          // Register the old → new ID mapping so future agent references to the
          // replaced sectionId resolve to the new section.
          // TODO: undo/redo does not restore incomingSectionIdMappings — after an undo the
          // page reverts to the old section but the mapping still points old→new, so a
          // subsequent agent operation using the old sectionId resolves to the now-missing
          // new section and silently no-ops. Fix by snapshotting/restoring mappings in
          // historyPast/historyFuture alongside the page snapshots.
          const newSectionId = replacedSection.id;
          set((state) => ({
            incomingSectionIdMappings: mergeIdMappings(state.incomingSectionIdMappings, {
              [resolvedSectionId]: newSectionId,
            }),
          }));

          handlePageEditorAction({
            type: PageEditorActionType.PAGE_SECTION_DELETE,
            payload: {
              sectionId: resolvedSectionId,
            },
          });

          handlePageEditorAction({
            type: PageEditorActionType.PAGE_SECTION_ADD,
            payload: {
              sectionId: replacedSection.id,
              section: replacedSection,
              insertRowPosition: replacedSection.rowPosition,
            },
          });
        },
      });
    },

    moveSectionUp: (sectionId) => {
      const resolvedSectionId = resolveIncomingSectionId(sectionId);
      const state = get();
      const sections = getSortedSections(getMainSections(state.page));
      const currentIndex = sections.findIndex((s) => s.id === resolvedSectionId);
      const swappedSectionId = currentIndex > 0 ? sections[currentIndex - 1].id : null;

      withHistory({
        action: () => moveSectionById(resolvedSectionId, 'up'),
        onSuccess: () => {
          const affectedSectionIds = swappedSectionId
            ? [resolvedSectionId, swappedSectionId]
            : [resolvedSectionId];
          handlePageEditorAction({
            type: PageEditorActionType.PAGE_SECTION_REORDER,
            payload: {
              sectionIds: affectedSectionIds,
            },
          });
        },
      });
    },

    moveSectionDown: (sectionId) => {
      const resolvedSectionId = resolveIncomingSectionId(sectionId);
      const state = get();
      const sections = getSortedSections(getMainSections(state.page));
      const currentIndex = sections.findIndex((s) => s.id === resolvedSectionId);
      const swappedSectionId =
        currentIndex < sections.length - 1 ? sections[currentIndex + 1].id : null;

      withHistory({
        action: () => moveSectionById(resolvedSectionId, 'down'),
        onSuccess: () => {
          const affectedSectionIds = swappedSectionId
            ? [resolvedSectionId, swappedSectionId]
            : [resolvedSectionId];
          handlePageEditorAction({
            type: PageEditorActionType.PAGE_SECTION_REORDER,
            payload: {
              sectionIds: affectedSectionIds,
            },
          });
        },
      });
    },

    /**
     * Reorders sections by the provided section IDs.
     * Uses reorderSectionsByIds as the single source of truth for state updates.
     */
    reorderSections: (sectionIds) => {
      const resolvedSectionIds = sectionIds.map(resolveIncomingSectionId);
      const state = get();
      if (!state.page) {
        return;
      }

      const currentSections = getMainSections(state.page);
      const sectionMap = new Map(currentSections.map((s) => [s.id, s]));

      // Track which sections will change row position (for efficient autosave).
      // Index in `sectionIds` is the target row index after reorder (0-based).
      const affectedSectionIds = resolvedSectionIds.filter((id, targetRowIndex) => {
        const section = sectionMap.get(id);
        return section && section.rowPosition !== targetRowIndex;
      });

      withHistory({
        action: () => reorderSectionsByIds(resolvedSectionIds),
        onSuccess: () => {
          if (affectedSectionIds.length > 0) {
            handlePageEditorAction({
              type: PageEditorActionType.PAGE_SECTION_REORDER,
              payload: {
                sectionIds: affectedSectionIds,
              },
            });
          }
        },
      });
    },

    moveSectionsToRowPositions: (moves) => {
      const state = get();
      if (!state.page) {
        return;
      }

      // Strategy: remove all moved sections, then insert them back in target order.
      const sections = getSortedSections(getMainSections(state.page));
      if (sections.length === 0 || moves.length === 0) {
        return;
      }

      const sectionMap = new Map(sections.map((section) => [section.id, section]));
      const originalIndexMap = new Map(sections.map((section, index) => [section.id, index]));

      const dedupedMoves = new Map<string, number>();
      moves.forEach(({ sectionId, targetRowPosition }) => {
        const resolvedSectionId = resolveIncomingSectionId(sectionId);
        if (!sectionMap.has(resolvedSectionId)) {
          return;
        }
        if (!Number.isFinite(targetRowPosition)) {
          return;
        }
        dedupedMoves.set(resolvedSectionId, targetRowPosition);
      });

      if (dedupedMoves.size === 0) {
        return;
      }

      const movedSections = sections.filter((section) => dedupedMoves.has(section.id));
      const remainingSections = sections.filter((section) => !dedupedMoves.has(section.id));

      const orderedMoves = movedSections
        .map((section) => ({
          section,
          targetRowPosition: dedupedMoves.get(section.id) ?? 0,
          originalIndex: originalIndexMap.get(section.id) ?? 0,
        }))
        .sort((a, b) => {
          if (a.targetRowPosition !== b.targetRowPosition) {
            return a.targetRowPosition - b.targetRowPosition;
          }
          return a.originalIndex - b.originalIndex;
        });

      const nextSections = [...remainingSections];
      orderedMoves.forEach(({ section, targetRowPosition }) => {
        const clampedIndex = Math.max(
          0,
          Math.min(nextSections.length, Math.trunc(targetRowPosition)),
        );
        nextSections.splice(clampedIndex, 0, section);
      });

      get().reorderSections(nextSections.map((section) => section.id));
    },

    beginHistoryGroup: () => {
      if (!isEditorWindow) return;
      if (historyGroupDepth === 0) {
        historyGroupSnapshot = prepareHistorySnapshot();
        historyGroupChanged = false;
        historyGroupCallbacks = [];
      }
      historyGroupDepth += 1;
    },

    endHistoryGroup: () => {
      if (!isEditorWindow || historyGroupDepth === 0) return;
      historyGroupDepth -= 1;
      if (historyGroupDepth > 0) return;

      const snapshot = historyGroupSnapshot;
      const changed = historyGroupChanged;
      const callbacks = historyGroupCallbacks;
      historyGroupSnapshot = null;
      historyGroupChanged = false;
      historyGroupCallbacks = [];

      if (changed) {
        commitHistoryEntry(snapshot, undefined);
        // Run outside commitHistoryEntry so a null snapshot can't swallow the autosave dispatches.
        for (const callback of callbacks) callback();
      }
    },

    undo: () => {
      if (!isEditorWindow) return;
      const state = get();
      if (state.pendingUndoConfirmation) return;
      const lastKind = state.historyOrder[state.historyOrder.length - 1];

      if (lastKind === 'site') {
        const adapter = state.siteHistoryAdapter;
        if (!adapter || !adapter.canUndo()) {
          // Stale 'site' entry (adapter missing or localStorage cleared) — drop
          // it so it doesn't permanently block earlier page history entries.
          set((s) => ({ ...s, historyOrder: s.historyOrder.slice(0, -1) }));
          return;
        }
        const ok = adapter.undo();
        if (!ok) return;
        set((s) => ({
          ...s,
          historyOrder: s.historyOrder.slice(0, -1),
          historyOrderFuture: s.historyOrderFuture.concat('site').slice(-HISTORY_LIMIT),
        }));
        return;
      }

      // Default: page undo (handles 'page' or empty kind for legacy entries)
      const previousPage = state.historyPast[state.historyPast.length - 1];
      const previousTargetId =
        state.historyPastTargets[state.historyPastTargets.length - 1] ?? null;
      if (!previousPage) return;

      if (!state.page || previousPage.id !== state.page.id) {
        set({
          pendingCrossPageHistoryAction: 'undo',
          pendingCrossPageHistoryPageId: previousPage.id,
        });
        router.visit(cmsPagesEditorPath(previousPage.id));
        return;
      }

      if (!state.pendingUndoConfirmation) {
        const proposals = getAiProposalsAdapter()?.getState().proposals ?? [];
        const conflictingIds = getConflictingProposalIdsForUndo(previousTargetId, proposals);
        if (conflictingIds.length > 0) {
          if (previousTargetId) scheduleScrollToPageEditorSection(previousTargetId);
          set({ pendingUndoConfirmation: true, pendingUndoProposalIds: conflictingIds });
          return;
        }
      }

      let beforePage: Page | null = null;
      let beforeSections: PageSection[] = [];
      let restoredPage: Page | null = null;
      let recordedTargetId: string | null = null;
      const restoreUndo = () => {
        set((s) => {
          if (!s.page || s.historyPast.length === 0) return s;
          beforeSections = getMainSections(s.page);
          const entryPage = s.historyPast[s.historyPast.length - 1];
          const entryTargetId = s.historyPastTargets[s.historyPastTargets.length - 1] ?? null;
          restoredPage = entryPage;
          const currentSnapshot = clonePageSnapshot(s.page);
          beforePage = currentSnapshot;
          recordedTargetId = entryTargetId;
          const syncPreview = shouldSyncPreviewNavigation(s.previewNavigationPage, entryPage.id);
          const restored = normalizePage({
            ...entryPage,
            slots: {
              ...entryPage.slots,
              header: s.page.slots?.header,
              footer: s.page.slots?.footer,
            } as Page['slots'],
          });
          return {
            ...s,
            page: restored,
            historyPast: s.historyPast.slice(0, -1),
            historyPastTargets: s.historyPastTargets.slice(0, -1),
            historyFuture: trimHistory([...s.historyFuture, currentSnapshot]),
            historyFutureTargets: trimHistory([...s.historyFutureTargets, entryTargetId]),
            historyOrder: s.historyOrder.slice(0, -1),
            historyOrderFuture: s.historyOrderFuture.concat('page').slice(-HISTORY_LIMIT),
            previewNavigationPage: syncPreview
              ? buildPreviewNavigation(entryPage)
              : s.previewNavigationPage,
            pendingUndoConfirmation: false,
            pendingUndoProposalIds: [],
          };
        });
      };

      const retainSectionId = resolveHistoryRetainSectionId(
        state.page,
        previousPage,
        previousTargetId,
      );
      const activePreviews = [...floatLayoutPreviewBaselines.values()];
      if (activePreviews.length > 0) {
        for (const baseline of activePreviews) {
          restoreFloatLayoutPreviewBaseline(baseline.sectionId, baseline.blockId);
        }
        return;
      }
      if (retainSectionId) {
        reorderSectionWithViewportRetain(retainSectionId, restoreUndo);
      } else {
        restoreUndo();
      }

      const undoScrollTargetId = resolveHistoryScrollTargetId(
        state.page,
        previousPage,
        previousTargetId,
      );
      if (undoScrollTargetId) {
        scheduleScrollToPageEditorSection(undoScrollTargetId);
      }

      if (restoredPage) {
        // undo/redo restores a whole-page snapshot, wiping proposal previews — re-apply them
        getAiProposalsAdapter()?.getState().reapplyPendingPreviews();
        const afterSections = getMainSections(restoredPage);
        const highlightTargetId =
          recordedTargetId ??
          (beforePage !== null
            ? resolveHistoryHighlightTargetId(beforePage, restoredPage)
            : resolveHistoryHighlightSectionId(beforeSections, afterSections));
        if (highlightTargetId) {
          markHistoryHighlight(highlightTargetId);
        }
        dispatchSnapshotDiff(beforeSections, afterSections);
        dispatchSnapshotMetadataDiff(beforePage, restoredPage);
      }
    },

    confirmUndo: async () => {
      const proposalIds = [...get().pendingUndoProposalIds];
      if (proposalIds.length > 0) {
        const aiAdapter = getAiProposalsAdapter();
        if (!aiAdapter) throw new Error('AI proposals adapter not registered');
        await aiAdapter.getState().rejectMany(proposalIds);
        const stillActive = aiAdapter.getState().proposals.some((p) => proposalIds.includes(p.id));
        if (stillActive) throw new Error('Failed to reject proposals');
      }
      set({ pendingUndoConfirmation: false, pendingUndoProposalIds: [] });
      get().undo();
    },

    cancelUndo: () => {
      set({ pendingUndoConfirmation: false, pendingUndoProposalIds: [] });
    },

    redo: () => {
      if (!isEditorWindow) return;
      const state = get();
      const nextKind = state.historyOrderFuture[state.historyOrderFuture.length - 1];

      if (nextKind === 'site') {
        const adapter = state.siteHistoryAdapter;
        if (!adapter || !adapter.canRedo()) {
          set((s) => ({ ...s, historyOrderFuture: s.historyOrderFuture.slice(0, -1) }));
          return;
        }
        const ok = adapter.redo();
        if (!ok) return;
        set((s) => ({
          ...s,
          historyOrder: s.historyOrder.concat('site').slice(-HISTORY_LIMIT),
          historyOrderFuture: s.historyOrderFuture.slice(0, -1),
        }));
        return;
      }

      const nextPage = state.historyFuture[state.historyFuture.length - 1];
      if (!nextPage) return;

      if (!state.page || nextPage.id !== state.page.id) {
        set({
          pendingCrossPageHistoryAction: 'redo',
          pendingCrossPageHistoryPageId: nextPage.id,
        });
        router.visit(cmsPagesEditorPath(nextPage.id));
        return;
      }

      const activePreviews = [...floatLayoutPreviewBaselines.values()];
      if (activePreviews.length > 0) {
        for (const baseline of activePreviews) {
          restoreFloatLayoutPreviewBaseline(baseline.sectionId, baseline.blockId);
        }
        return;
      }

      let beforePage: Page | null = null;
      let beforeSections: PageSection[] = [];
      let restoredPage: Page | null = null;
      let recordedTargetId: string | null = null;
      const restoreRedo = () => {
        set((s) => {
          if (!s.page || s.historyFuture.length === 0) return s;
          beforeSections = getMainSections(s.page);
          const entryPage = s.historyFuture[s.historyFuture.length - 1];
          const entryTargetId = s.historyFutureTargets[s.historyFutureTargets.length - 1] ?? null;
          restoredPage = entryPage;
          const currentSnapshot = clonePageSnapshot(s.page);
          beforePage = currentSnapshot;
          recordedTargetId = entryTargetId;
          const syncPreview = shouldSyncPreviewNavigation(s.previewNavigationPage, entryPage.id);
          const restored = normalizePage({
            ...entryPage,
            slots: {
              ...entryPage.slots,
              header: s.page.slots?.header,
              footer: s.page.slots?.footer,
            } as Page['slots'],
          });
          return {
            ...s,
            page: restored,
            historyPast: trimHistory([...s.historyPast, currentSnapshot]),
            historyPastTargets: trimHistory([...s.historyPastTargets, entryTargetId]),
            historyFuture: s.historyFuture.slice(0, -1),
            historyFutureTargets: s.historyFutureTargets.slice(0, -1),
            historyOrder: s.historyOrder.concat('page').slice(-HISTORY_LIMIT),
            historyOrderFuture: s.historyOrderFuture.slice(0, -1),
            previewNavigationPage: syncPreview
              ? buildPreviewNavigation(entryPage)
              : s.previewNavigationPage,
          };
        });
      };

      const retainSectionId = resolveHistoryRetainSectionId(
        state.page,
        nextPage,
        state.historyFutureTargets[state.historyFutureTargets.length - 1] ?? null,
      );
      if (retainSectionId) {
        reorderSectionWithViewportRetain(retainSectionId, restoreRedo);
      } else {
        restoreRedo();
      }

      const redoScrollTargetId = resolveHistoryScrollTargetId(
        state.page,
        nextPage,
        state.historyFutureTargets[state.historyFutureTargets.length - 1] ?? null,
      );
      if (redoScrollTargetId) {
        scheduleScrollToPageEditorSection(redoScrollTargetId);
      }

      if (restoredPage) {
        // undo/redo restores a whole-page snapshot, wiping proposal previews — re-apply them
        getAiProposalsAdapter()?.getState().reapplyPendingPreviews();
        const afterSections = getMainSections(restoredPage);
        const highlightTargetId =
          recordedTargetId ??
          (beforePage !== null
            ? resolveHistoryHighlightTargetId(beforePage, restoredPage)
            : resolveHistoryHighlightSectionId(beforeSections, afterSections));
        if (highlightTargetId) {
          markHistoryHighlight(highlightTargetId);
        }
        dispatchSnapshotDiff(beforeSections, afterSections);
        dispatchSnapshotMetadataDiff(beforePage, restoredPage);
      }
    },

    clearPageHistory: () => set((state) => clearPageHistoryState(state)),

    clearDeletedPageHistory: (pageId) =>
      set((state) => clearDeletedPageHistoryState(state, pageId)),

    clearPublishedHistory: () => set((state) => clearPublishedHistoryState(state)),

    setGeneratingSeo: (value) => set({ isGeneratingSeo: value }),

    updateSeoFromServer: (seo) =>
      set((state) => (state.page ? { page: { ...state.page, seo } } : state)),

    markHistoryHighlightTarget: (targetId) => markHistoryHighlight(targetId),

    clearHistoryHighlight: () => set({ historyHighlightSectionId: null }),

    clearHistory: () =>
      set({
        historyHighlightSectionId: null,
        historyHighlightVersion: 0,
        historyPast: [],
        historyFuture: [],
        historyPastTargets: [],
        historyFutureTargets: [],
        historyOrder: [],
        historyOrderFuture: [],
        pendingCrossPageHistoryAction: null,
        pendingCrossPageHistoryPageId: null,
        pendingUndoConfirmation: false,
        pendingUndoProposalIds: [],
      }),

    reset: () => {
      floatLayoutPreviewBaselines.clear();
      set((state) => ({
        ...state,
        page: null,
        isGeneratingSeo: false,
        previewNavigationPage: null,
        historyHighlightSectionId: null,
        historyHighlightVersion: 0,
        editingSlot: null,
        incomingSectionIdMappings: {},
        incomingBlockIdMappings: {},
        pendingUndoConfirmation: false,
        pendingUndoProposalIds: [],
      }));
    },
  };
});

if (isEditorWindow) {
  registerHistoryAccountIdProvider(() => usePageEditorStore.getState().historyAccountId);
  registerPageHistoryProvider(() => {
    const state = usePageEditorStore.getState();
    return {
      historyPast: state.historyPast,
      historyFuture: state.historyFuture,
      historyPastTargets: state.historyPastTargets,
      historyFutureTargets: state.historyFutureTargets,
      historyOrder: state.historyOrder,
      historyOrderFuture: state.historyOrderFuture,
    };
  });
  usePageEditorStore.subscribe(() => scheduleEditorHistoryWrite());
  registerPageHistoryApplier((persisted) => {
    usePageEditorStore.setState((state) => ({
      ...state,
      historyPast: persisted.historyPast,
      historyFuture: persisted.historyFuture,
      historyPastTargets: persisted.historyPastTargets,
      historyFutureTargets: persisted.historyFutureTargets,
      historyOrder: persisted.historyOrder,
      historyOrderFuture: persisted.historyOrderFuture,
      pendingCrossPageHistoryAction: null,
      pendingCrossPageHistoryPageId: null,
    }));
  });
}

const EMPTY_SECTIONS: PageSection[] = [];
export const MAX_SUGGESTIONS_PAGE_SUMMARY_SECTIONS = 100;

function buildSuggestionsPageSummaryFromPage(page: Page): SuggestionsPageSummary {
  const sections = getSortedSections(getMainSections(page))
    .slice(0, MAX_SUGGESTIONS_PAGE_SUMMARY_SECTIONS)
    .map((section) => ({
      id: section.id,
      sectionTemplateCode: section.sectionTemplateCode,
      variant: section.variant ?? null,
      rowPosition: section.rowPosition,
      visible: section.visible,
    }));

  return {
    pageId: page.id,
    pageType: page.pageType,
    sectionCount: sections.length,
    sections,
  };
}

/**
 * Selector to get main sections from the page editor store.
 * Usage: const sections = usePageEditorStore(selectMainSections);
 */
export const selectMainSections = (state: PageEditorStore): PageSection[] => {
  const sections = getMainSections(state.page);
  return sections.length === 0 ? EMPTY_SECTIONS : sections;
};

export const selectSuggestionsPageSummary = (
  state: PageEditorStore,
  pageId: string,
): SuggestionsPageSummary | null => {
  const page = state.page;
  if (!page || page.id !== pageId) {
    return null;
  }

  return buildSuggestionsPageSummaryFromPage(page);
};

export function getSuggestionsPageSummary(pageId: string): SuggestionsPageSummary | null {
  return selectSuggestionsPageSummary(usePageEditorStore.getState(), pageId);
}

export function getImageSuggestionsPageSummary(
  pageId: string,
  sectionId?: string,
  target?: ImageSuggestionTarget,
): ImageSuggestionsPageSummary | null {
  const page = usePageEditorStore.getState().page;
  if (!page || page.id !== pageId) return null;

  return buildImageSuggestionsPageSummary(page, sectionId, target);
}
